import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { PROJECT_TYPES } from "../common/real-estate-form-options";

@Component({
  selector: "app-land-services-project-estimate-details",
  templateUrl: "./land-services-project-estimate-details.component.html",
  styleUrls: ["./land-services-project-estimate-details.component.scss"],
})
export class LandServicesProjectEstimateDetailsComponent
  implements OnInit, OnChanges
{
  @Input() landServicesEstimateDetailsForm: FormGroup;
  @Input() reviewMode: boolean = false;
  @Input() acquisition: boolean = false;
  @Input() disposal: boolean = false;
  @Input() projectType: string = "";
  @Input() region: string = "";

  constructor() {}

  ngOnInit() {
    if (this.landServicesEstimateDetailsForm) {
      // Set default AFUDC percentage to 0.065%
      const capitalGroup = this.landServicesEstimateDetailsForm.get("capital");
      if (capitalGroup && this.acquisition && this.region !== "DE Florida") {
        capitalGroup.patchValue(
          { afudcPercentage: 0.065 },
          { emitEvent: false },
        );
      }

      // Calculate total whenever any field changes
      this.landServicesEstimateDetailsForm.valueChanges.subscribe(() => {
        this.calculateTotal();
        this.calculateEstimatedAFUDC();
        this.calculateCapitalTotal();
        this.calculateOMTotal();
        this.calculateTotalProjectCost();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes["acquisition"] || changes["disposal"]) &&
      this.landServicesEstimateDetailsForm
    ) {
      this.setExpenditureType();
      this.calculateTotal();
      this.calculateEstimatedAFUDC();
      this.calculateCapitalTotal();
      this.calculateOMTotal();
      this.calculateTotalProjectCost();
    }

    if (changes["projectType"] && this.landServicesEstimateDetailsForm) {
      this.setUtilityAccount();
    }
  }

  setExpenditureType() {
    if (this.acquisition) {
      this.landServicesEstimateDetailsForm.patchValue(
        {
          expenditureType: "Addition",
          chargeType: "Total Addition",
          transactionType: "Addition",
        },
        { emitEvent: false },
      );
    } else if (this.disposal) {
      this.landServicesEstimateDetailsForm.patchValue(
        {
          expenditureType: "Retirement",
          chargeType: "Total Retirement",
          transactionType: "Retirement",
          retirementUnit: "Land",
        },
        { emitEvent: false },
      );
    }
  }

  setUtilityAccount() {
    if (this.projectType) {
      const selectedProjectType = PROJECT_TYPES.find(
        (pt) => pt.value === this.projectType,
      );
      if (selectedProjectType && selectedProjectType.utilityAccount) {
        const patchData: any = {
          utilityAccount: selectedProjectType.utilityAccount,
        };

        // For acquisition, set retirementUnit equal to utilityAccount
        if (this.acquisition) {
          patchData.retirementUnit = selectedProjectType.utilityAccount;
        }

        this.landServicesEstimateDetailsForm.patchValue(patchData, {
          emitEvent: false,
        });
      }
    }
  }

  calculateTotal() {
    const formValue = this.landServicesEstimateDetailsForm.value;

    // Use purchasePrice for acquisition, negate salesProceeds for disposal
    const firstFieldValue = this.disposal
      ? -this.parseValue(formValue.salesProceeds)
      : this.parseValue(formValue.purchasePrice);

    const total =
      firstFieldValue +
      this.parseValue(formValue.appraisal) +
      this.parseValue(formValue.survey) +
      this.parseValue(formValue.title) +
      this.parseValue(formValue.closingCost) +
      this.parseValue(formValue.legalFees) +
      this.parseValue(formValue.brokerMarketingFees) +
      this.parseValue(formValue.environmentalReview) +
      this.parseValue(formValue.dukeLabor) +
      this.parseValue(formValue.buildingDemolition) +
      this.parseValue(formValue.environmentalCleanup);

    this.landServicesEstimateDetailsForm.patchValue(
      { total: total },
      { emitEvent: false },
    );
  }

  parseValue(value: string): number {
    // Remove commas and dollar signs, then parse to float
    if (!value) return 0;
    const cleanValue = value.toString().replace(/[$,]/g, "");
    return parseFloat(cleanValue) || 0;
  }

  calculateEstimatedAFUDC() {
    if (this.disposal) return;

    const capitalGroup = this.landServicesEstimateDetailsForm.get("capital");

    if (capitalGroup && this.region !== "DE Florida") {
      // Use fixed AFUDC percentage of 0.065%
      const afudcPercentage = 0.065;

      const estimateCostOfRequest = this.parseValue(
        capitalGroup.get("estimateCostOfRequest").value,
      );
      const previouslyApproved = this.parseValue(
        capitalGroup.get("previouslyApproved").value,
      );
      const subtotalProjectCost = estimateCostOfRequest + previouslyApproved;

      // Formula: (capital subtotal / 2) * (afudc percentage / 12) * 3
      const estimatedAfudcOfProject =
        (subtotalProjectCost / 2) * (afudcPercentage / 12) * 3;

      capitalGroup.patchValue(
        {
          afudcPercentage: afudcPercentage,
          estimatedAfudcOfProject: estimatedAfudcOfProject,
        },
        { emitEvent: false },
      );
    } else if (this.region === "DE Florida") {
      // Reset to 0 for DE Florida
      if (capitalGroup) {
        capitalGroup.patchValue(
          { estimatedAfudcOfProject: 0 },
          { emitEvent: false },
        );
      }
    }
  }

  calculateCapitalTotal() {
    const capitalGroup = this.landServicesEstimateDetailsForm.get("capital");

    if (capitalGroup) {
      const formValue = this.landServicesEstimateDetailsForm.value;
      const firstFieldValue = this.disposal
        ? -this.parseValue(formValue.salesProceeds)
        : this.parseValue(formValue.purchasePrice);

      const previouslyApproved = this.parseValue(
        capitalGroup.get("previouslyApproved").value,
      );

      const estimateCostOfRequest =
        firstFieldValue +
        this.parseValue(formValue.appraisal) +
        this.parseValue(formValue.survey) +
        this.parseValue(formValue.title) +
        this.parseValue(formValue.closingCost) +
        this.parseValue(formValue.legalFees) +
        this.parseValue(formValue.brokerMarketingFees) +
        this.parseValue(formValue.environmentalReview) +
        this.parseValue(formValue.dukeLabor) +
        this.parseValue(formValue.buildingDemolition) -
        previouslyApproved;

      const estimatedAfudcOfProject = this.parseValue(
        capitalGroup.get("estimatedAfudcOfProject").value,
      );

      const subtotalProjectCost = estimateCostOfRequest + previouslyApproved;
      const capitalTotal = subtotalProjectCost + estimatedAfudcOfProject;

      capitalGroup.patchValue(
        {
          estimateCostOfRequest: estimateCostOfRequest,
          subtotalProjectCost: subtotalProjectCost,
          total: capitalTotal,
        },
        { emitEvent: false },
      );
    }
  }

  calculateOMTotal() {
    const omGroup = this.landServicesEstimateDetailsForm.get("om");

    if (omGroup) {
      const previouslyApproved = this.parseValue(
        omGroup.get("previouslyApproved").value,
      );

      const formValue = this.landServicesEstimateDetailsForm.value;

      const estimateCostOfRequest =
        this.parseValue(formValue.environmentalCleanup) - previouslyApproved;

      const omTotal = estimateCostOfRequest + previouslyApproved;

      omGroup.patchValue(
        { estimateCostOfRequest: estimateCostOfRequest, total: omTotal },
        { emitEvent: false },
      );
    }
  }

  calculateTotalProjectCost() {
    const capitalGroup = this.landServicesEstimateDetailsForm.get("capital");
    const omGroup = this.landServicesEstimateDetailsForm.get("om");

    if (capitalGroup && omGroup) {
      const capitalTotal = this.parseValue(capitalGroup.get("total").value);
      const omTotal = this.parseValue(omGroup.get("total").value);

      const totalProjectCost = capitalTotal + omTotal;

      this.landServicesEstimateDetailsForm.patchValue(
        { totalProjectCost: totalProjectCost },
        { emitEvent: false },
      );
    }
  }
}
